import canAutoPlay from "can-autoplay";
import { inject, injectable } from "inversify";
import { PlayerCTAAPI, ScencarioElementOptions } from "../api/cta/PlayerCTAAPI";
import { VintomError } from "../api/model/error/VintomError";
import { PlayerState } from "../api/model/playerState/PlayerState";
import { I18n } from "../i18n/i18n";
import { CTA } from "../model/playerConfiguration/CTA";
import { FullPlayerConfiguration } from "../model/playerConfiguration/FullPlayerConfiguration";
import { ScenarioElement } from "../model/playerConfiguration/ScenarioElement";
import { IframeNotifierPlayerActionState } from "../model/playerState/IframeNotifierPlayerActionState";
import { IframeNotifierPlayerReady } from "../model/playerState/IframeNotifierPlayerReady";
import { IframeNotifierPlayerState } from "../model/playerState/IframeNotifierPlayerState";
import { IframeNotifierVintomError } from "../model/playerState/IframeNotifierVintomError";
import { ScenarioElementPlayOptions } from "../model/scenarioElementPlayOptions/ScenarioElementPlayOptions";
import { ScenarioGlobalState } from "../model/scenarioGlobalState/ScenarioGlobalState";
import { ScenarioPath } from "../model/scenarioPath/ScenarioPath";
import { ListenerHelper } from "../utils/ListenerHelper";
import { ConfigurationBuilder } from "./configurationBuilder/ConfigurationBuilder";
import { TYPES } from "./di/types";
import { ErrorReporter } from "./errorReporter/ErrorReporter";
import { IframeNotifier } from "./iframeNotifier/IframeNotifier";
import { PlayerLoader } from "./loader/playerLoader/PlayerLoader";
import { ScenarioLoader } from "./loader/scenarioLoader/ScenarioLoader";
import { AnalyticsCollector } from "./playerAnalytics/AnalyticsCollector";
import { PlayerManager, PlayerScenarioRunnerAPI } from "./PlayerManager";
import { PreferencesStorage } from "./preferencesStorage/PreferencesStorage";
import { ControlsLayerManager } from "./scenarioRunner/layerManager/ControlsLayerManager";
import { MusicLayerManager } from "./scenarioRunner/layerManager/MusicLayerManager";
import { PlayerContainerManager } from "./scenarioRunner/layerManager/PlayerContainerManager";
import { PosterLayerManager } from "./scenarioRunner/layerManager/PosterLayerManager";
import { VideoLayerManager } from "./scenarioRunner/layerManager/VideoLayerManager";
import { ScenarioRunner } from "./scenarioRunner/ScenarioRunner";
import { ThemeUpdater } from "./themeUpdater/ThemeUpdater";

@injectable()
export class PlayerManagerImpl implements PlayerManager, PlayerCTAAPI, PlayerScenarioRunnerAPI {

  private static DEFAULT_SCENARIO_ELEMENT_PLAY_OPTIONS: ScenarioElementPlayOptions = {
    isAutoplay: false,
    progress: 0
  };

  private static ANALYTICS_FASTER_RATE = 10;
  private static ANALYTICS_SLOWER_RATE = 20;
  private static ANALYTICS_SLOWER_RATE_THRESHOLD = 120;

  private static ANALYTICS_PROGRESS_25_THRESHOLD = 0.25;
  private static ANALYTICS_PROGRESS_50_THRESHOLD = 0.50;
  private static ANALYTICS_PROGRESS_75_THRESHOLD = 0.75;
  private static ANALYTICS_PROGRESS_100_THRESHOLD = 1;

  private i18n: I18n;

  private isPlayerStarted: boolean;
  private analyticsIntervalHandle: number;
  private playerConfig: FullPlayerConfiguration;
  private scenarioElementProgress: number;

  private onReadyCallbacks: ((payload: IframeNotifierPlayerReady) => void)[] = [];
  private onErrorCallbacks: ((error: IframeNotifierVintomError) => void)[] = [];
  private onPlayCallbacks: ((payload: IframeNotifierPlayerState) => void)[] = [];
  private onPauseCallbacks: ((payload: IframeNotifierPlayerState) => void)[] = [];
  private onFinishCallbacks: ((payload: IframeNotifierPlayerState) => void)[] = [];
  private onActionCallbacks: ((payload: IframeNotifierPlayerActionState) => void)[] = [];

  private videoCode: string;
  private configurationJSON;
  private configurationOverrideJSON;
  private personalizationJSON;

  constructor(
      @inject(TYPES.analyticsCollector.AnalyticsCollector) private analyticsCollector: AnalyticsCollector,
      @inject(TYPES.configurationBuilder.ConfigurationBuilder) private configurationBuilder: ConfigurationBuilder,
      @inject(TYPES.iframeNotifier.IframeNotifier) private iframeNotifier: IframeNotifier,
      @inject(TYPES.preferencesStorage.PreferencesStorage) private preferencesStorage: PreferencesStorage,
      @inject(TYPES.themeUpdater.ThemeUpdater) private themeUpdater: ThemeUpdater,
      @inject(TYPES.errorReporter.ErrorReporter) private errorReporter: ErrorReporter,
      @inject(TYPES.loader.playerLoader.PlayerLoader) private playerLoader: PlayerLoader,
      @inject(TYPES.loader.scenarioLoader.ScenarioLoader) private scenarioLoader: ScenarioLoader,
      @inject(TYPES.scenarioRunner.ScenarioRunner) private scenarioRunner: ScenarioRunner,
      @inject(TYPES.scenarioRunner.layerManager.ControlsLayerManager) private controlsLayerManager: ControlsLayerManager,
      @inject(TYPES.scenarioRunner.layerManager.PlayerContainerManager) private playerContainerManager: PlayerContainerManager,
      @inject(TYPES.scenarioRunner.layerManager.PosterLayerManager) private posterLayerManager: PosterLayerManager,
      @inject(TYPES.scenarioRunner.layerManager.VideoLayerManager) private videoLayerManager: VideoLayerManager,
      @inject(TYPES.scenarioRunner.layerManager.MusicLayerManager) private musicLayerManager: MusicLayerManager) {

    this.scenarioRunner.setPlayerAPIDelegate(this);
    this.scenarioLoader.setPlayerCTAAPIDelegate(this);
    this.registerIframeNotifier();
  }

  run(i18n: I18n, videoCode: string, configurationJSON: any, configurationOverrideJSON: any, personalizationJSON: any) {
    this.videoCode = videoCode;
    this.configurationJSON = configurationJSON;
    this.configurationOverrideJSON = configurationOverrideJSON;
    this.personalizationJSON = personalizationJSON;
    this.i18n = i18n;

    ListenerHelper.addEventListener("window", "beforeunload", () => this.onPlayerRemove());

    this.controlsLayerManager.showLoader();
    this.configurationBuilder.build(videoCode, configurationJSON, configurationOverrideJSON, personalizationJSON)
        .then((configuration) => this.playerConfig = configuration)
        .then(() => this.errorReporter.initialize(this.playerConfig.code))
        .then(() => this.analyticsCollector.initialize(
            this.playerConfig.code,
            this.playerConfig.videoDate,
            this.playerConfig.analyticsEndpoints,
            this.playerConfig.campaign,
            this.playerConfig.groupId))
        .then(() => this.themeUpdater.setTheme(this.playerConfig.skin))
        .then(() => this.start())
        .catch((error) => this.onError(error));
  }

  play(): void {
    this.scenarioRunner.play();
  }

  pause(): void {
    this.scenarioRunner.pause();
  }

  performAction(ctaId: number, actionId: number, ...args: any[]): void {
    this.scenarioRunner.performAction(ctaId, actionId, args);
  }

  moveToScenarioElement(id: number): void {
    const scenarioElement = this.playerConfig.findScenarioElement(id);
    if (scenarioElement) {
      this.loadScenarioElement(scenarioElement);
    }
  }

  prepareForRemove(): void {
    ListenerHelper.removeAllEventListeners();
    this.scenarioLoader.prepareForRemove();
    this.onPlayerRemove();
    this.clearAnalyticsInterval();
  }

  registerOnReady(callback: () => void): void {
    this.onReadyCallbacks.push(callback);
  }

  registerOnError(callback: (error: IframeNotifierVintomError) => void): void {
    this.onErrorCallbacks.push(callback);
  }

  registerOnPlay(callback: (payload: IframeNotifierPlayerState) => void): void {
    this.onPlayCallbacks.push(callback);
  }

  registerOnPause(callback: (payload: IframeNotifierPlayerState) => void): void {
    this.onPauseCallbacks.push(callback);
  }

  registerOnFinish(callback: (payload: IframeNotifierPlayerState) => void): void {
    this.onFinishCallbacks.push(callback);
  }

  registerOnAction(callback: (payload: IframeNotifierPlayerActionState) => void): void {
    this.onActionCallbacks.push(callback);
  }

  onReady(): void {
    this.analyticsCollector.logPlayerInit(this.getCurrentElementId());
    const readyState: IframeNotifierPlayerReady = {
      code: this.playerConfig.code,
      type: "vintomPlayerReady"
    };
    this.onReadyCallbacks.forEach((cb) => cb(readyState));
  }

  onError(error: VintomError): void {
    const isConfigurationParsed = !!this.playerConfig;
    if (!isConfigurationParsed) {
      console.error(error);
      return;
    }
    if (this.playerConfig.adminMode) {
      console.trace();
      console.error(error);
    }
    if (error.options.isLogAndSendToAnalytics) {
      const iframeError = error as IframeNotifierVintomError;
      iframeError.code = this.playerConfig.code;
      iframeError.type = "vintomPlayerError";
      this.onErrorCallbacks.forEach((cb) => {
        cb(iframeError);
      });
      this.analyticsCollector.logPlayerError(this.getCurrentElementId(), error);
    }
    if (error.options.isDisplayToUser) {
      this.controlsLayerManager.showMessage(error);
    }
  }

  onPlay(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();
    if (!this.isPlayerStarted) {
      this.analyticsCollector.logPlayerStart(this.getCurrentElementId());
      this.isPlayerStarted = true;
    }

    this.analyticsCollector.logScenarioElementPlaying(
        this.getCurrentElementId(),
        playbackData.videoType,
        playbackData.volume,
        playbackData.duration,
        playbackData.time);

    const state = this.getState() as IframeNotifierPlayerState;
    state.code = this.playerConfig.code;
    state.type = "vintomPlayerPlay";
    this.onPlayCallbacks.forEach((cb) => cb(state));
  }

  onPause(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();
    this.analyticsCollector.logScenarioElementPlaying(
        this.getCurrentElementId(),
        playbackData.videoType,
        playbackData.volume,
        playbackData.duration,
        playbackData.time,
        "PAUSED");

    const state = this.getState() as IframeNotifierPlayerState;
    state.code = this.playerConfig.code;
    state.type = "vintomPlayerPause";
    this.onPauseCallbacks.forEach((cb) => cb(state));
  }

  onSeek(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();
    this.analyticsCollector.logScenarioElementPlaying(
        this.getCurrentElementId(),
        playbackData.videoType,
        playbackData.volume,
        playbackData.duration,
        playbackData.time,
        "SEEKED");
  }

  onProgress(progress: number): void {

    if (this.scenarioElementProgress < PlayerManagerImpl.ANALYTICS_PROGRESS_25_THRESHOLD &&
        progress >= PlayerManagerImpl.ANALYTICS_PROGRESS_25_THRESHOLD) {
      this.analyticsCollector.logScenarioElementProgress(this.getCurrentElementId(), 25);
    }
    if (this.scenarioElementProgress < PlayerManagerImpl.ANALYTICS_PROGRESS_50_THRESHOLD &&
        progress >= PlayerManagerImpl.ANALYTICS_PROGRESS_50_THRESHOLD) {
      this.analyticsCollector.logScenarioElementProgress(this.getCurrentElementId(), 50);
    }
    if (this.scenarioElementProgress < PlayerManagerImpl.ANALYTICS_PROGRESS_75_THRESHOLD &&
        progress >= PlayerManagerImpl.ANALYTICS_PROGRESS_75_THRESHOLD) {
      this.analyticsCollector.logScenarioElementProgress(this.getCurrentElementId(), 75);
    }
    if (this.scenarioElementProgress < PlayerManagerImpl.ANALYTICS_PROGRESS_100_THRESHOLD &&
        progress >= PlayerManagerImpl.ANALYTICS_PROGRESS_100_THRESHOLD) {
      this.analyticsCollector.logScenarioElementProgress(this.getCurrentElementId(), 100);
    }

    this.scenarioElementProgress = progress;
  }

  onScenarioElementStart(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();
    this.analyticsCollector.logScenarioElementStart(
        this.getCurrentElementId(),
        playbackData.videoType,
        playbackData.volume,
        playbackData.duration,
    );

    this.startCollectingAnalyticsAtInterval();
  }

  onScenarioElementEnd(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();
    this.analyticsCollector.logScenarioElementPlaying(
        this.getCurrentElementId(),
        playbackData.videoType,
        playbackData.volume,
        playbackData.duration,
        playbackData.time,
        "FINISHED");

    this.clearAnalyticsInterval();

    if (this.getScenarioPath().isLastPart) {
      const state = this.getState() as IframeNotifierPlayerState;
      state.code = this.playerConfig.code;
      state.type = "vintomPlayerFinish";
      this.onFinishCallbacks.forEach((cb) => cb(state));
    } else {
      this.loadNextScenarioElement();
    }
  }

  onCTAShow(cta: CTA): void {
    if (!this.isTechnicalCTA(cta)) {
      this.analyticsCollector.logCTAShow(this.getCurrentElementId(), cta.id);
    }
  }

  isTechnicalCTA(cta: CTA): boolean {
    return cta.triggerType === "RANGE" && cta.visibilityRange && cta.visibilityRange.startTime === 0;
  }

  onCTAAction(ctaId: number, actionId: number, actionAdditionalData: object): void {
    const state = this.getState();
    const actionState: IframeNotifierPlayerActionState = {
      ...state,
      ctaId,
      actionId,
      actionAdditionalData,
      code: this.playerConfig.code,
      type: "vintomPlayerAction"
    };
    this.analyticsCollector.logCTAAction(this.getCurrentElementId(), ctaId, actionId, actionAdditionalData);
    this.onActionCallbacks.forEach((cb) => cb(actionState));
  }

  onPlayerRemove(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();
    if (playbackData) {
      this.analyticsCollector.logScenarioElementPlaying(
          this.getCurrentElementId(),
          playbackData.videoType,
          playbackData.volume,
          playbackData.duration,
          playbackData.time,
          "CLOSED");
    } else {
      // player may be removed before it's initialized
      this.analyticsCollector.logScenarioElementPlaying(
          this.getCurrentElementId(),
          undefined,
          undefined,
          undefined,
          undefined,
          "CLOSED");
    }

  }

  getState(): PlayerState {
    const playbackData = this.scenarioRunner.getPlaybackData();

    const state: PlayerState = {
      playing: playbackData.isPlaying,
      time: playbackData.time
    };

    if (!playbackData) {
      return undefined;
    }

    if (this.playerConfig.scenarioElements.length > 1) {
      state.scenarioElementId = this.getCurrentElementId();
    }

    if (this.getScenarioPath().hasMultipleParts) {
      state.part = this.getScenarioPath().currentPartNumber;
    }

    return state;
  }

  loadNextScenarioElement() {
    const nextId = this.getScenarioPath().currentElement.nextScenarioElementId;
    if (nextId !== undefined) {
      const nextElement = this.playerConfig.findScenarioElement(nextId);
      if (nextElement) {
        this.loadScenarioElement(nextElement, { isAutoplay: true });
      }
    }
  }

  loadScenarioElement(scenarioElement: ScenarioElement, options: ScenarioElementPlayOptions = {}) {
    options = {
      ...PlayerManagerImpl.DEFAULT_SCENARIO_ELEMENT_PLAY_OPTIONS,
      ...options
    };
    if (scenarioElement.music) {
      this.musicLayerManager.changeSource(scenarioElement.music.musicUrl);
      this.musicLayerManager.setMusicTime(scenarioElement.music.musicStartTime, scenarioElement.music.musicEndTime);
      this.videoLayerManager.setMusicTime(scenarioElement.music.musicStartTime, scenarioElement.music.musicEndTime);
    }
    this.controlsLayerManager.showLoader();

    const scenarioResources = this.scenarioLoader
        .loadResources(this.playerConfig, scenarioElement);
    scenarioResources.catch((e) => this.onError(e));
    const scenarioGlobalState = this.scenarioRunner.getScenarioGlobalState() ||
        this.getInitialScenarioGlobalState();

    return this.scenarioRunner.run(scenarioGlobalState, scenarioElement, scenarioResources, options);
  }

  jumpToScenarioElement(
      scenarioElementId: number,
      options?: ScencarioElementOptions,
  ) {
    const progress = options && options.progress || 0;
    const start = options && options.start !== undefined ? options.start : true;

    const scenarioElement = this.playerConfig.findScenarioElement(
        scenarioElementId
    );
    if (scenarioElement) {
      this.scenarioRunner.jumpToScenarioElement(
          scenarioElement,
          progress,
          start
      );
    } else {
      this.onError(new VintomError("runtimeError", `Attempted to load non existing scenario element (id: ${scenarioElementId})`));
    }
  }

  replay() {
    this.loadScenarioElement(this.getScenarioPath().initialElement, { isAutoplay: true });
  }

  logUserError(error: VintomError): void {
    this.errorReporter.sendErrorLog(error);
  }

  private registerIframeNotifier() {
    this.onReadyCallbacks.push(this.iframeNotifier.notify);
    this.onErrorCallbacks.push(this.iframeNotifier.notify);
    this.onPlayCallbacks.push(this.iframeNotifier.notify);
    this.onPauseCallbacks.push(this.iframeNotifier.notify);
    this.onFinishCallbacks.push(this.iframeNotifier.notify);
    this.onActionCallbacks.push(this.iframeNotifier.notify);
  }

  private getInitialScenarioGlobalState(): ScenarioGlobalState {
    const storedPreferences = this.preferencesStorage.getPreferences();
    return ScenarioGlobalState.ininitialState(
        this.playerConfig.scenarioElements,
        this.playerConfig.parts,
        this.playerConfig.aspectRatio,
        this.playerConfig.mutedStart,
        this.playerConfig.autoRefreshBeforeReady,
        this.playerConfig.subtitlesActiveOnStart,
        storedPreferences);
  }

  private getScenarioPath(): ScenarioPath {
    const scenarioGlobalState = this.scenarioRunner.getScenarioGlobalState();
    return scenarioGlobalState && scenarioGlobalState.scenarioPath;
  }

  private getCurrentElementId(): number {
    const scenarioPath = this.getScenarioPath();
    return scenarioPath && scenarioPath.currentElementId;
  }

  private callback(loadingPageVisible: boolean, counter: number, timeToReload: number) {
    if (counter > 0) {
      this.configurationBuilder.build(this.videoCode, this.configurationJSON, this.configurationOverrideJSON, this.personalizationJSON)
          .then((conf) => {
            this.playerConfig = conf;
            this.checkStatus(loadingPageVisible, counter + 1, timeToReload + 3000);
          });
    } else {
      this.checkStatus(loadingPageVisible, counter + 1, timeToReload + 3000);
    }
  }

  private checkStatus(loadingPageVisible: boolean, counter: number, timeToReload: number) {
    switch (this.playerConfig.status) {
      case "DELETED":
        this.playerLoader.loadResources(this.playerConfig).then(() => {
          this.controlsLayerManager.showMessage(this.i18n.videoDeleted);
        });
        return;
      case "PREPARING":
        if (loadingPageVisible === true && counter < 7) {
          this.playerLoader.loadResources(this.playerConfig).then(() => {
            this.controlsLayerManager.showLoadingPage(true);
          });
          if (!this.videoCode) {
            console.error("Video code must be set on initialize for autoRefreshBeforeReady function");
          } else {
            setTimeout(() => this.callback(loadingPageVisible, counter, timeToReload), timeToReload);
          }
        } else if (loadingPageVisible === true && counter >= 7) {
          this.controlsLayerManager.showLoadingPage(false);
          this.playerLoader.loadResources(this.playerConfig).then(() => {
            this.controlsLayerManager.showMessage(this.i18n.videoInPreparationLoadingPage);
          });
        } else if (loadingPageVisible === false) {
          this.controlsLayerManager.showLoadingPage(false);
          this.playerLoader.loadResources(this.playerConfig).then(() => {
            this.controlsLayerManager.showMessage(this.i18n.videoInPreparation);
          });
        }
        return;
      case "READY":
        this.controlsLayerManager.showLoadingPage(false);
        this.controlsLayerManager.showLoader();
        const initialScEl = this.playerConfig.scenarioElements.find((scEl) => scEl.initial);
        this.posterLayerManager.showInitialPoster(initialScEl.poster, this.playerConfig.aspectRatio);
        if (initialScEl.music) {
          this.musicLayerManager.changeSource(initialScEl.music.musicUrl);
          this.musicLayerManager.setMusicTime(initialScEl.music.musicStartTime, initialScEl.music.musicEndTime);
          this.videoLayerManager.setMusicTime(initialScEl.music.musicStartTime, initialScEl.music.musicEndTime);

        }
        this.loadAndInitializeLayers()
            .then(() => {
              if (this.playerConfig.autoplay || initialScEl.isCTAOnlyElement && !initialScEl.isCTAOnlyPlaybackStoppingElement) {
                return this.checkAutoplaySupported();
              }
              return false;
            })
            .then((isAutoplay) => this.loadScenarioElement(initialScEl, { isAutoplay }))
            .then(() => this.controlsLayerManager.hideLoader())
            .then(() => this.onReady());
        return;
      default:
        throw new VintomError("runtimeError", `Unsupported film status '${status}'`);
    }}

  private start(): void {
    const loadingPageVisible = this.playerConfig.autoRefreshBeforeReady;
    this.controlsLayerManager.setConfiguration(this.playerConfig.playerControls, this.playerConfig.adminMode);
    this.playerContainerManager.setConfiguration(this.playerConfig.playerControls);
    this.checkStatus(loadingPageVisible, 0, 0);
  }

  private loadAndInitializeLayers(): Promise<void> {
    return this.playerLoader.loadResources(this.playerConfig)
        .then(() => {
          if (this.playerConfig.hasScenarioElementWithVideo) {
            this.videoLayerManager.initializeLayer();
          }
          this.musicLayerManager.initializeLayer();
        });
  }

  private clearAnalyticsInterval() {
    if (this.analyticsIntervalHandle !== undefined) {
      clearInterval(this.analyticsIntervalHandle);
    }
  }

  private startCollectingAnalyticsAtInterval() {
    this.clearAnalyticsInterval();

    const duration = this.scenarioRunner.getPlaybackData().duration;
    const interval = duration < PlayerManagerImpl.ANALYTICS_SLOWER_RATE_THRESHOLD ?
        PlayerManagerImpl.ANALYTICS_FASTER_RATE : PlayerManagerImpl.ANALYTICS_SLOWER_RATE;

    this.analyticsIntervalHandle = window.setInterval(
        () => {
          const playbackData = this.scenarioRunner.getPlaybackData();
          if (playbackData.isPlaying) {
            this.analyticsCollector.logScenarioElementPlaying(
                this.getCurrentElementId(),
                playbackData.videoType,
                playbackData.volume,
                playbackData.duration,
                playbackData.time);
          }
        },
        interval * 1000);
  }

  private checkAutoplaySupported(): Promise<boolean> {
    return canAutoPlay.video()
        .then(({ result }) => result)
        .catch(() => false);
  }
}
